import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  updateProfile,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, setDoc, getDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase/config';

const AuthContext = createContext(null);

// Localhost development mode: bypass Firebase for testing
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const DEV_MODE = isLocalhost && process.env.NODE_ENV === 'development';

// Mock user for development
const MOCK_USER_ID = 'dev-user-' + Math.random().toString(36).substr(2, 9);
const MOCK_USER = {
  uid: MOCK_USER_ID,
  email: 'gabriela.ormazabal@gormaran-marketing.com',
  displayName: 'Gabriela (Dev)',
  photoURL: '',
};

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function createUserProfile(user, displayName) {
    const userRef = doc(db, 'users', user.uid);
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      const persona = localStorage.getItem('gormaran_user_persona') || null;
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        displayName: displayName || user.displayName || '',
        photoURL: user.photoURL || '',
        subscription: 'free',
        usageCount: 0,
        usageResetDate: new Date(),
        persona,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
    const updated = await getDoc(userRef);
    setUserProfile(updated.data());
  }

  async function register(email, password, displayName) {
    if (DEV_MODE) {
      // Development mode: create mock user and profile
      console.log('📝 DEV MODE: Creating mock user for testing');
      const mockUser = { ...MOCK_USER, email, displayName };
      setCurrentUser(mockUser);
      await createUserProfile(mockUser, displayName);
      return mockUser;
    }
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(user, { displayName });
    await createUserProfile(user, displayName);
    return user;
  }

  async function login(email, password) {
    if (DEV_MODE) {
      // Development mode: allow any email/password and load mock user profile
      console.log('🔓 DEV MODE: Logging in as mock user (bypassing Firebase)');
      const mockUser = { ...MOCK_USER, email };
      setCurrentUser(mockUser);
      await createUserProfile(mockUser, mockUser.displayName);
      return { user: mockUser };
    }
    const result = await signInWithEmailAndPassword(auth, email, password);
    await refreshUserProfile(result.user.uid);
    return result;
  }

  async function signInWithGoogle() {
    const result = await signInWithPopup(auth, googleProvider);
    // Pass displayName explicitly — result.user.displayName is guaranteed
    // to be populated at this point (directly from the popup response),
    // unlike the user object received later in onAuthStateChanged.
    await createUserProfile(result.user, result.user.displayName || '');
    return result;
  }

  async function logout() {
    setUserProfile(null);
    return signOut(auth);
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  async function refreshUserProfile(uid) {
    const userRef = doc(db, 'users', uid || currentUser?.uid);
    const snap = await getDoc(userRef);
    if (snap.exists()) {
      setUserProfile(snap.data());
      return snap.data();
    }
    return null;
  }

  useEffect(() => {
    let unsubscribeProfile = null;

    if (DEV_MODE) {
      // Development mode: check if mock user was previously logged in
      const storedDevUser = localStorage.getItem('devUserLogged');
      if (storedDevUser) {
        console.log('🔓 DEV MODE: Restoring previous session');
        setCurrentUser(MOCK_USER);
        createUserProfile(MOCK_USER, MOCK_USER.displayName).then(() => {
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
      return () => {};
    }

    let unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);

      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (user) {
        // Real-time listener — badge updates automatically when webhook changes Firestore
        // (e.g. subscription cancelled at period end → Free badge appears without reload)
        const userRef = doc(db, 'users', user.uid);
        unsubscribeProfile = onSnapshot(userRef, (snap) => {
          const profile = snap.exists() ? snap.data() : null;
          setUserProfile(profile);
          setLoading(false);

          // ContentSquare: identify user + set cohort variables
          window._uxa = window._uxa || [];
          window._uxa.push(['identify', user.uid]);
          window._uxa.push(['setCustomVariable', 1, 'PlanTier',    profile?.subscription || 'free', 3]);
          window._uxa.push(['setCustomVariable', 2, 'HasPaid',     String(profile?.subscription === 'paid'), 3]);
          window._uxa.push(['setCustomVariable', 3, 'CreditsUsed', String(profile?.usageCount || 0), 3]);
          window._uxa.push(['setCustomVariable', 4, 'Registered',  'true', 3]);
        }, (err) => {
          console.error('AuthContext profile listener error:', err);
          setLoading(false);
        });
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) unsubscribeProfile();
    };
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    register,
    login,
    logout,
    signInWithGoogle,
    resetPassword,
    refreshUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
